import { useAuth, API_ORIGIN } from '@yeshuman/sdk'
import type { TextFilterOperator, CandidateMatchBand } from '@yeshuman/sdk'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export interface Opportunity {
  id: string
  title: string
  description: string
  location: string
  organisation_name: string
  /** Set on employer list rows so create/edit can target the correct org API path */
  organisation_slug?: string
  organisation_logo_url?: string | null
  created_at?: string
  updated_at?: string
  publication_status?: 'draft' | 'published' | 'closed' | 'archived'
  public_slug?: string | null
  published_at?: string | null
  is_featured?: boolean
  kind?: 'managed' | 'marketplace'
  provider?: string
  company_display_name?: string
  redirect_url?: string
  marketplace_status?: string
  skills: OpportunitySkill[]
  experiences: OpportunityExperience[]
  employment_type?: string
  work_mode?: string
  city?: string
  country_code?: string
  salary_visibility?: string
  salary_min?: number | null
  salary_max?: number | null
  salary_currency?: string
  salary_period?: string
  match_percent?: number
  match_band?: CandidateMatchBand
}

export interface OpportunitySkill {
  id: string
  skill_name: string
  requirement_type: string
}

export interface OpportunityExperience {
  id: string
  description: string
}

export interface PaginatedOpportunities {
  results: Opportunity[]
  page: number
  page_size: number
  total: number
  has_next: boolean
}

export interface OpportunitiesFilters {
  q?: string
  organisation?: string
  organisation_operator?: TextFilterOperator
  location?: string
  location_operator?: TextFilterOperator
  mode?: 'keyword' | 'semantic' | 'hybrid'
  listing_type?: 'all' | 'featured' | 'new'
  /** TalentCo candidate homepage Featured ranking (API-only; omit on jobs browse). */
  placement?: 'candidate_home'
  /** Comma-separated skill names; API requires all listed skills (AND). */
  skills?: string[]
  work_mode?: string[]
  employment_type?: string[]
  published_since?: '24h' | '7d' | '30d'
  source?: Array<'managed' | 'marketplace'>
  sort?: 'match' | 'updated_desc' | 'updated_asc' | 'title_asc' | 'title_desc'
  page?: number
  page_size?: number
}

function buildOpportunitiesParams(filters: OpportunitiesFilters): URLSearchParams {
  const params = new URLSearchParams()

  if (filters.q) params.append('q', filters.q)
  if (filters.organisation) {
    params.append('organisation', filters.organisation)
    if (filters.organisation_operator) params.append('organisation_op', filters.organisation_operator)
  }
  if (filters.location) {
    params.append('location', filters.location)
    if (filters.location_operator) params.append('location_op', filters.location_operator)
  }
  if (filters.mode) params.append('mode', filters.mode || 'hybrid')
  if (filters.listing_type && filters.listing_type !== 'all') {
    params.append('listing_type', filters.listing_type)
  }
  if (filters.placement) {
    params.append('placement', filters.placement)
  }
  if (filters.skills && filters.skills.length > 0) {
    params.append('skills', filters.skills.join(','))
  }
  if (filters.work_mode && filters.work_mode.length > 0) {
    params.append('work_mode', filters.work_mode.join(','))
  }
  if (filters.employment_type && filters.employment_type.length > 0) {
    params.append('employment_type', filters.employment_type.join(','))
  }
  if (filters.published_since) {
    params.append('published_since', filters.published_since)
  }
  if (filters.source && filters.source.length > 0) {
    params.append('source', filters.source.join(','))
  }
  if (filters.sort && filters.sort !== 'updated_desc') {
    params.append('sort', filters.sort)
  }
  if (filters.page) params.append('page', filters.page.toString())
  if (filters.page_size) params.append('page_size', (filters.page_size || 20).toString())

  return params
}

export function useOpportunities(
  filters: OpportunitiesFilters = {},
  options: { enabled?: boolean; staleTime?: number } = {}
) {
  const { enabled: queryEnabled = true, staleTime = 5 * 60 * 1000 } = options
  const { token } = useAuth()
  const queryClient = useQueryClient()

  /** Bumped when browse list payload shape changes so we do not reuse pre-upgrade TanStack cached rows (see EV-97 browse schema). */
  const listSchemaVersion = 'browseRowMatch'

  const queryKey = ['opportunities', listSchemaVersion, filters] as const

  const opportunitiesQuery = useQuery({
    queryKey,
    queryFn: async (): Promise<PaginatedOpportunities> => {
      const params = buildOpportunitiesParams(filters)
      const url = `${API_ORIGIN}/api/opportunities/?${params.toString()}`

      const response = await fetch(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        cache: 'no-store',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch opportunities')
      }

      return response.json()
    },
    enabled: queryEnabled,
    staleTime,
  })

  // Function to load more results
  const loadMore = () => {
    if (!opportunitiesQuery.data?.has_next) return

    const nextPage = (opportunitiesQuery.data.page || 1) + 1
    const nextFilters = { ...filters, page: nextPage }

    queryClient.prefetchQuery({
      queryKey: ['opportunities', listSchemaVersion, nextFilters],
      queryFn: async (): Promise<PaginatedOpportunities> => {
        const params = buildOpportunitiesParams(nextFilters)
        const url = `${API_ORIGIN}/api/opportunities/?${params.toString()}`

        const response = await fetch(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        })

        if (!response.ok) {
          throw new Error('Failed to fetch opportunities')
        }

        return response.json()
      },
    })
  }

  return {
    opportunities: opportunitiesQuery.data?.results || [],
    pagination: {
      page: opportunitiesQuery.data?.page || 1,
      page_size: opportunitiesQuery.data?.page_size || 20,
      total: opportunitiesQuery.data?.total || 0,
      has_next: opportunitiesQuery.data?.has_next || false,
    },
    isLoading: opportunitiesQuery.isLoading,
    error: opportunitiesQuery.error,
    loadMore,
    refetch: opportunitiesQuery.refetch,
  }
}
